import { FormattedMessage, useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { Head } from '@xing-com/crate-startpage-logged-out-head';
import type { Locale } from '@xing-com/crate-xinglet';
import { useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import { GridContainer, GridItem } from '@xing-com/grid';
import { Section, OnlyOnSmall } from '@xing-com/layout-primitives';
import { Hero } from '@xing-com/typography';

import illustrationLeft from './assets/illustration-left.svg';
import illustrationMobile from './assets/illustration-mobile.svg';
import illustrationRight from './assets/illustration-right.svg';
import {
  LanguagesGridItem,
  SideIllustration,
  Illustration,
  DesktopOnlyGridItem,
} from './language.styles';

type LanguageLink = {
  language: Locale;
  activeLanguage: string;
};

const LANGUAGES_COPY: Record<Locale, string> = {
  de: 'WELCOME_LANGUAGE_SELECT_GERMAN_LABEL',
  en: 'WELCOME_LANGUAGE_SELECT_ENGLISH_LABEL',
  es: 'WELCOME_LANGUAGE_SELECT_SPANISH_LABEL',
  fr: 'WELCOME_LANGUAGE_SELECT_FRENCH_LABEL',
  it: 'WELCOME_LANGUAGE_SELECT_ITALIAN_LABEL',
};

const LanguageLink: React.FC<LanguageLink> = ({ language, activeLanguage }) => {
  const href = `/${language}`;
  const isActive = language === activeLanguage;

  return (
    <GridItem span={12} spanMedium={4}>
      <TextButton
        size="large"
        fontWeight={isActive ? undefined : 'regular'}
        href={href}
        data-qa={isActive ? 'active' : undefined}
      >
        <FormattedMessage id={LANGUAGES_COPY[language] || 'en'} />
      </TextButton>
    </GridItem>
  );
};

export const Language: React.FC = () => {
  const { locale } = useIntl();
  const { visibleLocales: languages } = useRuntimeConfig();

  return (
    <Section noBlockPadding>
      <Head page="languagePage" />
      <GridContainer variant="wide">
        <DesktopOnlyGridItem span={3}>
          <SideIllustration src={illustrationLeft} alt="" aria-hidden />
        </DesktopOnlyGridItem>
        <LanguagesGridItem span={12} spanMedium={6}>
          <Hero size="small">
            <FormattedMessage id="WELCOME_LANGUAGE_SELECT_HEADLINE" />
          </Hero>
          <GridContainer variant="wide">
            {languages.map((language) => (
              <LanguageLink
                key={language}
                activeLanguage={locale}
                language={language}
              />
            ))}
          </GridContainer>
        </LanguagesGridItem>
        <DesktopOnlyGridItem span={3}>
          <SideIllustration src={illustrationRight} alt="" aria-hidden />
        </DesktopOnlyGridItem>
      </GridContainer>
      <OnlyOnSmall>
        <Illustration src={illustrationMobile} alt="" aria-hidden />
      </OnlyOnSmall>
    </Section>
  );
};
